import { useEffect, useMemo, useState } from "react";
import { BriefcaseBusiness, CalendarDays, CheckCircle2, FolderKanban, GraduationCap } from "lucide-react";

import AppShell from "@/components/AppShell";
import ProfileWorkspaceLayout from "@/components/ProfileWorkspaceLayout";
import { getAllTasks, getLearningAssignments, getMyWorkshopAttendance, getProjects } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const toDate = (value) => {
  const date = new Date(value || "");
  return Number.isNaN(date.getTime()) ? null : date;
};

const monthKeyFromDate = (value) => {
  const date = toDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const roleLabel = (value = "") => String(value || "member").replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());

const healthMeta = (score = 0) => {
  const safe = Number(score || 0);
  if (safe >= 80) return { label: "Healthy", className: "chip chip-ok" };
  if (safe >= 65) return { label: "Watch", className: "chip chip-warn" };
  if (safe >= 50) return { label: "At risk", className: "chip chip-risk" };
  return { label: "Critical", className: "chip chip-crit" };
};

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const shiftMonthKey = (baseKey, delta) => {
  const [yearPart, monthPart] = baseKey.split("-").map(Number);
  const date = new Date(yearPart, monthPart - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export default function ProfileStatsPage({ user, onLogout }) {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [learningRows, setLearningRows] = useState([]);
  const [workshopAttendance, setWorkshopAttendance] = useState({ total_attended: 0, records: [] });

  useEffect(() => {
    const load = async () => {
      try {
        const baseMonth = currentMonthKey();
        const monthKeys = [baseMonth, shiftMonthKey(baseMonth, -1), shiftMonthKey(baseMonth, -2), shiftMonthKey(baseMonth, -3)];
        const [tasksRes, projectsRes, workshopAttendanceRes, ...learningResults] = await Promise.all([
          getAllTasks({ assigned_scope: "assigned_to_you", limit: 1000 }),
          getProjects(),
          getMyWorkshopAttendance(),
          ...monthKeys.map((month) => getLearningAssignments({ month })),
        ]);

        const projectIdsFromTasks = new Set((tasksRes || []).map((task) => task.project_id).filter(Boolean));
        const scopedProjects = (projectsRes || []).filter((project) => projectIdsFromTasks.has(project.id));

        setTasks(tasksRes || []);
        setProjects(scopedProjects.length ? scopedProjects : (projectsRes || []).slice(0, 4));
        setWorkshopAttendance(workshopAttendanceRes || { total_attended: 0, records: [] });
        setLearningRows(learningResults.flat().filter((row) => row.member_email === user.email));
      } catch (error) {
        toast.error(error.message);
      }
    };
    load();
  }, [user.email]);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const currentMonth = currentMonthKey();
  const currentYear = now.getFullYear();

  const completedTasks = useMemo(() => tasks.filter((row) => row.status === "completed"), [tasks]);

  const tasksDoneThisMonth = useMemo(
    () => completedTasks.filter((row) => monthKeyFromDate(row.updated_at || row.created_at) === currentMonth).length,
    [completedTasks, currentMonth],
  );

  const tasksDoneThisYear = useMemo(
    () => completedTasks.filter((row) => {
      const date = toDate(row.updated_at || row.created_at);
      return date && date.getFullYear() === currentYear;
    }).length,
    [completedTasks, currentYear],
  );

  const overdueTasks = useMemo(
    () => tasks.filter((row) => row.status !== "completed" && toDate(row.deadline) && toDate(row.deadline) < today),
    [tasks, today],
  );

  const coursesDoneThisMonth = useMemo(
    () => learningRows.filter((row) => row.status === "verified" && monthKeyFromDate(row.verified_at || row.updated_at || row.created_at) === currentMonth).length,
    [currentMonth, learningRows],
  );

  const coursesDoneThisYear = useMemo(
    () => learningRows.filter((row) => {
      if (row.status !== "verified") return false;
      const date = toDate(row.verified_at || row.updated_at || row.created_at);
      return date && date.getFullYear() === currentYear;
    }).length,
    [currentYear, learningRows],
  );

  const totalCoursesThisYear = useMemo(
    () => learningRows.filter((row) => {
      const date = toDate(row.created_at || row.updated_at);
      return date && date.getFullYear() === currentYear;
    }).length,
    [currentYear, learningRows],
  );

  const recentTasks = useMemo(
    () => [...tasks].sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || ""))).slice(0, 5),
    [tasks],
  );

  const tenureMonths = useMemo(() => {
    const createdAt = toDate(user.company_joined_at || user.created_at);
    if (!createdAt) return 0;
    return Math.max(0, (now.getFullYear() - createdAt.getFullYear()) * 12 + (now.getMonth() - createdAt.getMonth()));
  }, [now, user.company_joined_at, user.created_at]);

  return (
    <AppShell user={user} onLogout={onLogout}>
      <ProfileWorkspaceLayout user={user} onLogout={onLogout} title="My Work" subtitle="Your personal scorecard — tasks, projects and learning.">
        <section className="profile-kpi-grid" data-testid="profile-stats-kpi-grid">
          <article className="profile-kpi-card green" data-testid="profile-stats-kpi-tasks-month">
            <div className="label">Tasks done</div>
            <div className="value">{tasksDoneThisMonth}</div>
            <div className="sub">This month</div>
          </article>
          <article className="profile-kpi-card red" data-testid="profile-stats-kpi-overdue">
            <div className="label">Overdue</div>
            <div className="value">{overdueTasks.length}</div>
            <div className="sub">Action needed</div>
          </article>
          <article className="profile-kpi-card" data-testid="profile-stats-kpi-active-projects">
            <div className="label">Active projects</div>
            <div className="value">{projects.length}</div>
            <div className="sub">Across your assignments</div>
          </article>
          <article className="profile-kpi-card indigo" data-testid="profile-stats-kpi-courses-month">
            <div className="label">Courses done</div>
            <div className="value">{coursesDoneThisMonth}</div>
            <div className="sub">This month</div>
          </article>
        </section>

        <section className="profile-card" data-testid="profile-stats-year-overview-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><CalendarDays className="h-4 w-4" /> Year overview</div>
            <span className="chip chip-mute" data-testid="profile-stats-year-chip">Jan - {now.toLocaleDateString(undefined, { month: "short" })} {currentYear}</span>
          </div>

          <div className="profile-year-grid" data-testid="profile-stats-year-grid">
            <div>
              <div className="profile-field-label">Tasks completed</div>
              <div className="profile-field-value" data-testid="profile-stats-year-tasks-value">{tasksDoneThisYear}</div>
              <div className="progress"><span style={{ width: `${Math.min(100, Math.round((tasksDoneThisYear / Math.max(1, tasks.length || 1)) * 100))}%` }} /></div>
            </div>
            <div>
              <div className="profile-field-label">Courses completed</div>
              <div className="profile-field-value" data-testid="profile-stats-year-courses-value">{coursesDoneThisYear}</div>
              <div className="progress"><span style={{ width: `${Math.min(100, Math.round((coursesDoneThisYear / Math.max(1, totalCoursesThisYear || 1)) * 100))}%`, background: "#6366F1" }} /></div>
            </div>
            <div>
              <div className="profile-field-label">Member tenure</div>
              <div className="profile-field-value" data-testid="profile-stats-tenure-value">{tenureMonths}mo</div>
              <div className="progress"><span style={{ width: `${Math.min(100, (tenureMonths / 60) * 100)}%`, background: "#64748B" }} /></div>
            </div>
          </div>
        </section>

        <section className="profile-card" data-testid="profile-stats-projects-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><FolderKanban className="h-4 w-4" /> Active projects</div>
            <span className="chip chip-mute" data-testid="profile-stats-projects-count-chip">{projects.length} projects</span>
          </div>

          <div className="profile-list" data-testid="profile-stats-projects-list">
            {projects.length === 0 ? (
              <div className="profile-empty" data-testid="profile-stats-projects-empty">No active projects found.</div>
            ) : (
              projects.slice(0, 6).map((project) => {
                const openCount = tasks.filter((task) => task.project_id === project.id && task.status !== "completed").length;
                const health = healthMeta(project.health_score || 0);
                return (
                  <article className="profile-list-row" key={project.id} data-testid={`profile-stats-project-row-${project.id}`}>
                    <div>
                      <div className="title">{project.name}</div>
                      <div className="meta">{roleLabel(project.user_role || "member")}</div>
                    </div>
                    <div className="meta">{openCount} open tasks</div>
                    <div className="meta" style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                      <span className={health.className}>{health.label}</span>
                      <span>{project.health_score || 0}</span>
                    </div>
                    <a href={`/project/${project.id}`} className="profile-inline-link" data-testid={`profile-stats-project-open-link-${project.id}`}>Open →</a>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section className="profile-card" data-testid="profile-stats-recent-tasks-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><BriefcaseBusiness className="h-4 w-4" /> Recent tasks</div>
            <a href="/tasks" className="profile-inline-link" data-testid="profile-stats-recent-tasks-view-all-link">View all →</a>
          </div>

          <div className="profile-list" data-testid="profile-stats-recent-tasks-list">
            {recentTasks.length === 0 ? (
              <div className="profile-empty" data-testid="profile-stats-recent-tasks-empty">No tasks yet.</div>
            ) : (
              recentTasks.map((task) => (
                <article className="profile-list-row" key={task.id} data-testid={`profile-stats-recent-task-row-${task.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {task.status === "completed" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <GraduationCap className="h-4 w-4 text-slate-500" />}
                    <div>
                      <div className="title">{task.task}</div>
                      <div className="meta">Due {formatDate(task.deadline)}</div>
                    </div>
                  </div>
                  <span className={task.status === "completed" ? "chip chip-ok" : task.status === "in_progress" ? "chip chip-brand" : "chip chip-warn"}>
                    {task.status.replace("_", " ")}
                  </span>
                </article>
              ))
            )}
          </div>
        </section>

        <section className="profile-card" data-testid="profile-stats-workshops-card">
          <div className="profile-card-head">
            <div className="profile-card-title"><GraduationCap className="h-4 w-4" /> Workshops attended</div>
            <span className="chip chip-mute" data-testid="profile-stats-workshops-count-chip">{workshopAttendance.total_attended || 0} sessions</span>
          </div>

          <div className="profile-list" data-testid="profile-stats-workshops-list">
            {(workshopAttendance.records || []).length === 0 ? (
              <div className="profile-empty" data-testid="profile-stats-workshops-empty">No workshop attendance recorded yet.</div>
            ) : (
              (workshopAttendance.records || []).slice(0, 8).map((item) => (
                <article className="profile-list-row" key={item.attendance_id} data-testid={`profile-stats-workshop-row-${item.attendance_id}`}>
                  <div>
                    <div className="title">{item.workshop_title}</div>
                    <div className="meta">{item.manager_name || "Manager"} · {item.quarter || "Quarter"}</div>
                  </div>
                  <div className="meta">{formatDate(item.conducted_date)}</div>
                  <span className="chip chip-brand">{item.duration_mins || 0} mins</span>
                </article>
              ))
            )}
          </div>
        </section>
      </ProfileWorkspaceLayout>
    </AppShell>
  );
}
